import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { imprestsApi, type ImprestSheetDto } from '../../api/imprests';
import { workerContractsApi } from '../../api/workerContracts';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';
import AccountSelect from '../../components/AccountSelect';

/**
 * P1f — Avansai ir atostogų atsargos: imprest sheets post their own JE
 * (D avansai / C mokėtinas DU) and feed the wage generator's pending
 * deduction; the holiday reserve is a delta run (legs reversed when it
 * shrinks). Mirror workspaces: read-only (ADR-004).
 */
export default function ImprestSheetsPage() {
  const isMirror = useIsMirror();
  const queryClient = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: sheets, isLoading } = useQuery({
    queryKey: ['imprests', year, month],
    queryFn: () => imprestsApi.list(year, month),
  });
  const { data: contracts } = useQuery({
    queryKey: ['worker-contracts'],
    queryFn: workerContractsApi.list,
    staleTime: 5 * 60 * 1000,
  });

  // New imprest form state.
  const [workerId, setWorkerId] = useState<number | null>(null);
  const [amount, setAmount] = useState('');

  // Holiday reserve form state.
  const [reserveDate, setReserveDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [accountCosts, setAccountCosts] = useState<number | null>(null);
  const [accountReserve, setAccountReserve] = useState<number | null>(null);
  const [reserveChange, setReserveChange] = useState('');

  const reload = () => queryClient.invalidateQueries({ queryKey: ['imprests', year, month] });

  const addRow = async () => {
    if (!workerId || !amount) {
      setError('Pasirinkite darbuotoją ir įveskite sumą.');
      return;
    }
    const contract = (contracts ?? []).find(c => c.workerId === workerId);
    setBusy(true);
    setError('');
    try {
      await imprestsApi.save(year, month, `${year}-${String(month).padStart(2, '0')}-15`, [
        // Merge with existing draft rows.
        ...((sheets ?? []).find(s => !s.isPosted)?.items ?? []).map(i => ({
          workerId: i.workerId,
          contractNumber: i.contractNumber,
          contractSerial: i.contractSerial,
          amount: i.amount,
        })),
        { workerId, contractNumber: contract?.contractNumber ?? 1, contractSerial: contract?.contractSerial ?? '', amount: Number(amount) },
      ]);
      setAmount('');
      await reload();
    } catch (e: any) {
      setError(e.message || 'Nepavyko išsaugoti');
    } finally {
      setBusy(false);
    }
  };

  const post = async (sheetId: number) => {
    setBusy(true);
    setError('');
    try {
      await imprestsApi.post(sheetId);
      await reload();
    } catch (e: any) {
      setError(e.message || 'Nepavyko užregistruoti');
    } finally {
      setBusy(false);
    }
  };

  const markPaid = async (sheetId: number, itemId: number) => {
    setBusy(true);
    setError('');
    try {
      await imprestsApi.markItemPaid(sheetId, itemId, new Date().toISOString().slice(0, 10));
      await reload();
    } catch (e: any) {
      setError(e.message || 'Nepavyko pažymėti');
    } finally {
      setBusy(false);
    }
  };

  const runReserve = async () => {
    if (!accountCosts || !accountReserve || !reserveChange || Number(reserveChange) === 0) {
      setError('Atsargoms reikia abiejų sąskaitų ir ne nulinio pokyčio.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await imprestsApi.runHolidayReserve(reserveDate, accountCosts, accountReserve, Number(reserveChange));
      setReserveChange('');
    } catch (e: any) {
      setError(e.message || 'Nepavyko įrašyti atsargų');
    } finally {
      setBusy(false);
    }
  };

  useReportScreen(
    `Avansai ${year}-${String(month).padStart(2, '0')}: ${sheets?.length ?? 0} žiniaraščiai`,
    { recordCount: sheets?.length ?? 0, filters: { metai: String(year), menuo: String(month) } },
  );

  const draft = (sheets ?? []).find(s => !s.isPosted);
  const posted = (sheets ?? []).filter(s => s.isPosted);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Avansai</h1>
          <p className="text-sm text-gray-500">
            Avanso žiniaraštis registruoja D avansai / C mokėtinas DU; išmokėjimas mažina būsimą žiniaraščio atskaitą.
          </p>
        </div>
        <Link
          to="/payroll"
          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
        >
          ← Darbo užmokestis
        </Link>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
        <label>
          Metai:{' '}
          <input type="number" value={year} min={2000} max={2100} onChange={e => setYear(Number(e.target.value))} />
        </label>
        <label>
          Mėnuo:{' '}
          <select value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
      </div>

      {isMirror && <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti.</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Draft sheet + add-row form. */}
          {draft && !isMirror && (
            <div className="card">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">
                Juodraštis {draft.documentNumber}
              </h2>
              <ImprestTable sheet={draft} onMarkPaid={() => {}} markPaidDisabled={true} />
              <div className="flex items-end gap-2 mt-3">
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Darbuotojas</span>
                  <select value={workerId ?? ''} onChange={e => setWorkerId(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">— darbuotojas —</option>
                    {(contracts ?? []).map(c => (
                      <option key={c.workerId} value={c.workerId}>{c.workerName}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Suma</span>
                  <input style={{ width: '7rem' }} value={amount} onChange={e => setAmount(e.target.value)} />
                </label>
                <button onClick={addRow} disabled={busy} className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
                  Pridėti
                </button>
                <button onClick={() => post(draft.id)} disabled={busy} className="px-4 py-1.5 text-sm font-semibold text-white bg-green-700 rounded-lg hover:bg-green-800 disabled:opacity-40">
                  Registruoti
                </button>
              </div>
            </div>
          )}
          {!draft && !isMirror && (
            <div className="card" style={{ padding: '1rem' }}>
              <p className="text-sm text-gray-500 mb-2">Šio mėnesio avansų žiniaraščio nėra.</p>
              <div className="flex items-end gap-2">
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Darbuotojas</span>
                  <select value={workerId ?? ''} onChange={e => setWorkerId(e.target.value ? Number(e.target.value) : null)}>
                    <option value="">— darbuotojas —</option>
                    {(contracts ?? []).map(c => (
                      <option key={c.workerId} value={c.workerId}>{c.workerName}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Suma</span>
                  <input style={{ width: '7rem' }} value={amount} onChange={e => setAmount(e.target.value)} />
                </label>
                <button onClick={addRow} disabled={busy} className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
                  Sukurti
                </button>
              </div>
            </div>
          )}

          {posted.map(s => (
            <div key={s.id} className="card">
              <h2 className="text-sm font-semibold text-gray-700 mb-2">
                {s.documentNumber} <span className="text-xs text-gray-400">užregistruotas (JE {s.journalEntryId})</span>
              </h2>
              <ImprestTable sheet={s} onMarkPaid={(itemId) => markPaid(s.id, itemId)} markPaidDisabled={isMirror} />
            </div>
          ))}

          {/* Holiday reserve run. */}
          {!isMirror && (
            <div className="card" style={{ padding: '1rem' }}>
              <h2 className="text-sm font-semibold text-gray-700 mb-2">Atostogų atsargos</h2>
              <div className="flex items-end gap-2 flex-wrap">
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Data</span>
                  <input type="date" value={reserveDate} onChange={e => setReserveDate(e.target.value)} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Sąnaudų sąskaita</span>
                  <AccountSelect value={accountCosts} onChange={setAccountCosts} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Atsargų sąskaita</span>
                  <AccountSelect value={accountReserve} onChange={setAccountReserve} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-500 mb-1">Pokytis (+/−)</span>
                  <input style={{ width: '7rem' }} value={reserveChange} onChange={e => setReserveChange(e.target.value)} />
                </label>
                <button onClick={runReserve} disabled={busy} className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
                  Įrašyti
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ImprestTable({
  sheet,
  onMarkPaid,
  markPaidDisabled,
}: {
  sheet: ImprestSheetDto;
  onMarkPaid: (itemId: number) => void;
  markPaidDisabled: boolean;
}) {
  const total = sheet.items.reduce((s, i) => s + i.amount, 0);
  return (
    <table className="data-table">
      <thead>
        <tr>
          <th>Darbuotojas</th>
          <th>Sutartis</th>
          <th style={{ textAlign: 'right' }}>Suma</th>
          <th>Išmokėta</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {sheet.items.map(i => (
          <tr key={i.id}>
            <td>{i.workerName}</td>
            <td>{i.contractSerial} {i.contractNumber}</td>
            <td style={{ textAlign: 'right' }}><MoneyCell value={i.amount} /></td>
            <td className="text-xs text-gray-500">{i.paidOutDate?.slice(0, 10) ?? '—'}</td>
            <td>
              {sheet.isPosted && !i.paidOutDate && !markPaidDisabled && (
                <button onClick={() => onMarkPaid(i.id)}>Išmokėta</button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="total-row">
          <td colSpan={2}><strong>Viso</strong></td>
          <td style={{ textAlign: 'right' }}><strong><MoneyCell value={total} /></strong></td>
          <td colSpan={2}></td>
        </tr>
      </tfoot>
    </table>
  );
}

